import React, { useEffect, useRef, useState } from 'react';
import { useWorldStore } from '../../state/worldStore';
import { audioEngine } from '../../services/audioEngine';
import { AUTH_TOKEN } from '../../config';
import { api } from '../../services/api';
import useCallSession from '../../hooks/useCallSession';

// PhoneVideo — calling another MEMBER, face to face, from the phone.
//
// Khari, 2026-08-04: "If I call someone, it will send them a push notification
// on their phone.. they get the notification with a link to answer the call..
// when they answer, the peer to peer begins (starts with the option to appear
// as character, with no streaming until you are ready)."
//
// THE PRIVACY RULE IS THE FEATURE, and it is why this screen exists at all: the
// camera does not go on the wire until you say so. Answering opens a GREEN ROOM
// — you pick who you appear as, you see your own preview, and nothing leaves
// the device — and only "Go live" starts the session. Once you have chosen a
// character, the peer sees a blank card until the swap is rendering; if the
// renderer fails they keep seeing the blank card. The real camera reaches the
// other person by exactly one path: you pressing the button that says so.
//
// Everything below the green room is the WEB'S session hook, ported (see
// useCallSession) — same routes, same signaling, same X2 swap. This file is the
// phone's version of the studio's panel, nothing more.

const wrap = { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' };
const pad = { padding: '10px 14px' };
const label = { font: '600 10px system-ui', letterSpacing: 1.1, textTransform: 'uppercase', opacity: 0.55 };
const input = {
  width: '100%', boxSizing: 'border-box', marginTop: 6, padding: '9px 11px', borderRadius: 10,
  border: '1px solid rgba(255,233,196,0.22)', background: 'rgba(255,255,255,0.06)',
  color: '#fff', font: '15px system-ui', outline: 'none',
};
const primary = (bg = '#2fd06a') => ({
  width: '100%', marginTop: 10, padding: '11px', borderRadius: 12, border: 'none',
  cursor: 'pointer', font: '800 14px system-ui', background: bg, color: '#08120a',
});
const ghost = {
  width: '100%', marginTop: 8, padding: '10px', borderRadius: 12, cursor: 'pointer',
  border: '1px solid rgba(255,233,196,0.28)', background: 'transparent',
  color: '#ffe9c4', font: '700 13px system-ui',
};
const bigCircle = (bg) => ({
  width: 58, height: 58, borderRadius: '50%', border: 'none', cursor: 'pointer',
  background: bg, color: '#0b0b10', font: '20px system-ui', display: 'grid', placeItems: 'center',
});

function Video({ stream, muted, mirrored, style }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (el.srcObject !== stream) el.srcObject = stream || null;
  }, [stream]);
  return (
    <video
      ref={ref}
      autoPlay
      playsInline
      muted={muted}
      style={{ width: '100%', height: '100%', objectFit: 'cover', transform: mirrored ? 'scaleX(-1)' : 'none', ...style }}
    />
  );
}

export default function PhoneVideo() {
  const videoCall = useWorldStore((s) => s.videoCall);
  const openVideoCall = useWorldStore((s) => s.openVideoCall);
  const closeVideoCall = useWorldStore((s) => s.closeVideoCall);
  const myAvatarId = useWorldStore((s) => s.myAvatarId);

  const session = useCallSession({ token: AUTH_TOKEN });
  const { phase, error, mediaNote, code, invited, inviteNote, avatarCam, peer,
    localStream, remoteStream, micOn, camOn, toggleMic, toggleCam,
    start, join, leave, revealRealCamera, appearAsAvatar } = session;

  // ── the green room ────────────────────────────────────────────────────────
  // Who you appear as is decided BEFORE anything connects, because after is too
  // late: the first frame of a call is the one that gives you away.
  const [handle, setHandle] = useState('');
  const [asAvatar, setAsAvatar] = useState(null);   // { avatarId, name } | null
  const [mine, setMine] = useState([]);             // this member's own artists
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState('');
  const [joinCode, setJoinCode] = useState('');

  useEffect(() => {
    if (!AUTH_TOKEN) return;
    // The same list every other surface reads — `myAvatars` is findByUserId.
    api.myAvatars().then((d) => {
        const list = (d.avatars || []).map((a) => ({ avatarId: a.avatar_id, name: a.name }));
        setMine(list);
        // Lead with whoever you are walking around as — the world already knows
        // which of your artists you are being today.
        const here = list.find((a) => String(a.avatarId) === String(myAvatarId));
        setAsAvatar(here || list[0] || null);
      })
      .catch(() => setMine([]));
  }, [myAvatarId]);

  // A call opened from a ring (or a ?call= link) arrives with a code to answer.
  const answering = videoCall?.code && videoCall.answering;

  // The world's own audio stands down for the whole call — this is a person
  // talking to a person, and a music bed underneath it is noise.
  useEffect(() => {
    if (phase !== 'live' && phase !== 'connecting') return undefined;
    audioEngine.duck(true, true);
    return () => audioEngine.duck(false);
  }, [phase]);

  // Leaving the app ends the call rather than orphaning a camera.
  const leaveRef = useRef(leave);
  leaveRef.current = leave;
  useEffect(() => () => { leaveRef.current?.(); }, []);

  const idle = phase === 'idle' || phase === 'ended' || phase === 'error';

  const placeCall = async () => {
    const h = handle.trim().replace(/^@/, '');
    if (!h) { setNote('Who are you calling?'); return; }
    setBusy(true); setNote('');
    try {
      await start({ inviteHandle: h, appearAs: asAvatar });
    } finally { setBusy(false); }
  };

  const answer = async () => {
    setBusy(true); setNote('');
    try {
      await join(videoCall.code, { appearAs: asAvatar });
    } finally { setBusy(false); }
  };

  // ── green room ────────────────────────────────────────────────────────────
  if (idle) {
    return (
      <div style={wrap}>
        <div style={{ ...pad, flex: 1, minHeight: 0, overflowY: 'auto' }}>
          {error && <div style={{ font: '600 12px system-ui', color: '#ff9d7d', marginBottom: 8 }}>{error}</div>}

          <div style={label}>You appear as</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 }}>
            {mine.map((a) => (
              <button
                key={a.avatarId}
                onClick={() => setAsAvatar(a)}
                style={{
                  cursor: 'pointer', borderRadius: 999, padding: '6px 10px', font: '600 12px system-ui',
                  border: `1px solid ${asAvatar?.avatarId === a.avatarId ? '#7dff9d' : 'rgba(255,233,196,0.25)'}`,
                  background: asAvatar?.avatarId === a.avatarId ? 'rgba(125,255,157,0.16)' : 'transparent',
                  color: asAvatar?.avatarId === a.avatarId ? '#7dff9d' : '#ffe9c4',
                }}
              >{a.name}</button>
            ))}
            <button
              onClick={() => setAsAvatar(null)}
              style={{
                cursor: 'pointer', borderRadius: 999, padding: '6px 10px', font: '600 12px system-ui',
                border: `1px solid ${!asAvatar ? '#7dff9d' : 'rgba(255,233,196,0.25)'}`,
                background: !asAvatar ? 'rgba(125,255,157,0.16)' : 'transparent',
                color: !asAvatar ? '#7dff9d' : '#ffe9c4',
              }}
            >Just me</button>
          </div>
          <div style={{ font: '11px system-ui', opacity: 0.55, marginTop: 6, lineHeight: 1.45 }}>
            {asAvatar
              ? `They see ${asAvatar.name}. Your camera drives the performance and never reaches them.`
              : 'They see your real camera.'}
          </div>

          {answering ? (
            <>
              <div style={{ ...label, marginTop: 16 }}>Incoming</div>
              <div style={{ font: '700 15px system-ui', marginTop: 4 }}>
                {videoCall.peerName || 'Someone'} is calling
              </div>
              <button style={primary()} onClick={answer} disabled={busy}>{busy ? 'Answering…' : 'Answer'}</button>
              <button style={ghost} onClick={closeVideoCall}>Decline</button>
            </>
          ) : (
            <>
              <div style={{ ...label, marginTop: 16 }}>Call an artist</div>
              <input
                style={input}
                value={handle}
                onChange={(e) => setHandle(e.target.value)}
                placeholder="@handle"
                onKeyDown={(e) => { if (e.key === 'Enter') placeCall(); }}
              />
              <button style={primary()} onClick={placeCall} disabled={busy}>{busy ? 'Calling…' : 'Call'}</button>
              {note && <div style={{ font: '11.5px system-ui', color: '#ff9d7d', marginTop: 8 }}>{note}</div>}

              {/* JOINING BY CODE, which depends on nothing being delivered.
                  A ring needs the other person signed into the world; a push
                  needs the mobile app; a notification needs them to look. The
                  code needs a person reading it out — which is why every
                  conference product still has one, and why this is how the
                  feature can be tested at all today. */}
              <div style={{ ...label, marginTop: 18 }}>Or answer with a code</div>
              <input
                style={input}
                value={joinCode}
                onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
                placeholder="ABCD12"
                onKeyDown={(e) => { if (e.key === 'Enter' && joinCode.trim()) join(joinCode.trim(), { appearAs: asAvatar }); }}
              />
              <button
                style={{ ...primary('#7dd3ff'), opacity: joinCode.trim() ? 1 : 0.5 }}
                onClick={() => join(joinCode.trim(), { appearAs: asAvatar })}
                disabled={busy || !joinCode.trim()}
              >Join</button>
            </>
          )}
        </div>
      </div>
    );
  }

  // ── in a call (or waiting for them to pick up) ────────────────────────────
  const waiting = phase === 'waiting' || phase === 'starting';
  return (
    <div style={wrap}>
      <div style={{ flex: 1, minHeight: 0, position: 'relative', background: '#08070c', overflow: 'hidden' }}>
        {remoteStream
          ? <Video stream={remoteStream} muted={false} />
          : (
            <div style={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', textAlign: 'center', padding: 18 }}>
              <div>
                <div style={{ font: '700 14px system-ui' }}>
                  {waiting ? (invited?.name ? `Ringing ${invited.name}…` : 'Waiting for them to join…') : 'Connecting…'}
                </div>
                {code && (
                  <>
                    <div style={{ font: '600 10px system-ui', letterSpacing: 1.1, textTransform: 'uppercase', opacity: 0.45, marginTop: 12 }}>
                      Read them this code
                    </div>
                    <div style={{ font: '800 22px system-ui', letterSpacing: 3, marginTop: 2 }}>{code}</div>
                  </>
                )}
                {/* THE INVITE'S FATE, said out loud. A call whose invite failed
                    looks exactly like a call nobody has answered yet, and the
                    difference is everything: one is waiting, the other is never
                    going to ring. */}
                {inviteNote
                  ? <div style={{ font: '11.5px system-ui', color: '#ff9d7d', marginTop: 10, lineHeight: 1.45 }}>{inviteNote}</div>
                  : invited
                    ? <div style={{ font: '11px system-ui', opacity: 0.5, marginTop: 10, lineHeight: 1.45 }}>
                        {invited.notified
                          ? `${invited.name} was notified${invited.notificationId ? ` (#${invited.notificationId})` : ''}. If they are not in the city, it is waiting in their notifications.`
                          : `Reached ${invited.name}, but the notification did not save — read them the code.`}
                      </div>
                    : null}
              </div>
            </div>
          )}

        {/* Your own picture, small — and it shows what THEY see, so an avatar
            swap is verifiable at a glance rather than taken on trust. */}
        {localStream && (
          <div style={{
            position: 'absolute', right: 8, bottom: 8, width: 74, height: 100, borderRadius: 10,
            overflow: 'hidden', border: '1px solid rgba(255,255,255,0.25)', background: '#000',
          }}>
            <Video stream={localStream} muted mirrored />
          </div>
        )}

        {avatarCam.status !== 'off' && (
          <div style={{
            position: 'absolute', left: 8, top: 8, borderRadius: 999, padding: '4px 9px',
            background: 'rgba(8,12,9,0.8)', border: '1px solid rgba(125,255,157,0.4)',
            font: '600 10.5px system-ui', color: '#7dff9d',
          }}>
            {avatarCam.status === 'live' ? `as ${avatarCam.name}`
              : avatarCam.status === 'preparing' ? 'becoming your character…'
                : 'character failed — you are hidden'}
          </div>
        )}
      </div>

      {mediaNote && <div style={{ ...pad, font: '11px system-ui', color: '#ffd27d' }}>{mediaNote}</div>}

      <div style={{ padding: '10px 0 14px', display: 'flex', justifyContent: 'center', gap: 14 }}>
        <button onClick={toggleMic} style={bigCircle(micOn ? 'rgba(255,233,196,0.16)' : '#ff5065')} aria-label="Toggle mic">
          {micOn ? '🎙' : '🔇'}
        </button>
        <button onClick={() => { leave(); closeVideoCall(); }} style={bigCircle('#ff5065')} aria-label="End call">✕</button>
        <button onClick={toggleCam} style={bigCircle(camOn ? 'rgba(255,233,196,0.16)' : '#ff5065')} aria-label="Toggle camera">
          {camOn ? '📷' : '🚫'}
        </button>
      </div>

      {/* BOTH WAYS. Dropping the mask is still an explicit act — never
          automatic, never a fallback when the renderer fails — but it is not a
          one-way door: mid-call you flick between your face and your character,
          and each direction is its own deliberate press. Going BACK is only
          offered once there is an avatar to go back to. */}
      {avatarCam.status !== 'off' ? (
        <button style={{ ...ghost, margin: '0 14px 12px', width: 'auto' }} onClick={revealRealCamera}>
          Show my real camera
        </button>
      ) : asAvatar ? (
        <button style={{ ...ghost, margin: '0 14px 12px', width: 'auto' }} onClick={() => appearAsAvatar(asAvatar)}>
          Appear as {asAvatar.name}
        </button>
      ) : null}
    </div>
  );
}
